import * as React from 'react';
import Draggable from 'react-draggable';
import {
    arrayMove,
    SortableContainer,
    SortableElement,
    SortEnd
} from 'react-sortable-hoc';
import { ColumnInput } from './ColumnInput';
import { DragHandle } from './DragHandle';
import { sortColumnsByName, sortColumnsByOrder } from '../utils/utils';

/**
 * The props of the ColumnPicker component
 */
export interface ColumnPickerProps {
    /**
     * The available columns
     */
    columnOptions: ColumnOption[];
    /**
     * The callback function to call when the Save button is clicked
     */
    onSave: (columnOptions: ColumnOption[]) => void;
    /**
     * The callback function to invoke when the Rest button is clicked
     */
    onReset: () => void;
    /**
     * The dialog's default x position
     */
    defaultX?: number;
    /**
     * The dialog's default y position
     */
    defaultY?: number;
}

/**
 * The state of the ColumnPicker component
 */
export interface ColumnPickerState {
    /**
     * The active columns
     */
    activeColumns: ColumnOption[];
    /**
     * The inactive columns
     */
    inactiveColumns: ColumnOption[];
    /**
     * The currently displayed active columns
     */
    tempActiveColumns: ColumnOption[];
    /**
     * The currently displayed inactive columns
     */
    tempInactiveColumns: ColumnOption[];
    /**
     * If the ColumnPicker component is visible
     */
    visible: boolean;
}

/**
 * SortableItem component from react-sortable-hoc
 */
const SortableItem = SortableElement(({ value }: { value: JSX.Element }) => (
    <div style={{ verticalAlign: 'middle', border: '1px solid #DDD' }}>
        <DragHandle />
        {value}
    </div>
));

/**
 * SortableList component from react-sortable-hoc
 */
const SortableList = SortableContainer(
    ({ items }: { items: JSX.Element[] }) => {
        return (
            <div>
                {items.map((value, index) => (
                    <SortableItem
                        key={`item-${index}`}
                        index={index}
                        value={value}
                    />
                ))}
            </div>
        );
    }
);

/**
 * This component allows a user to rearrange and select columns
 * that can be displayed in a table (JQuery DataTables/Orchard Data Tables)
 */
export class ColumnPicker extends React.Component<
    ColumnPickerProps,
    ColumnPickerState
> {
    constructor(props: ColumnPickerProps) {
        super(props);

        const activeColumns = props.columnOptions
            .filter(column => column.active)
            .sort(sortColumnsByOrder);
        const inactiveColumns = props.columnOptions
            .filter(column => !column.active)
            .sort(sortColumnsByName);

        this.state = {
            activeColumns,
            inactiveColumns,
            tempActiveColumns: [...activeColumns],
            tempInactiveColumns: [...inactiveColumns],
            visible: true
        };
    }

    render() {
        if (!this.state.visible) {
            return null;
        }

        const activeItems = this.state.tempActiveColumns.map(item =>
            this.buildColumnInput(item)
        );

        const inactiveItems = this.state.tempInactiveColumns.map(item =>
            this.buildColumnInput(item)
        );

        // The dialog style
        const dialogStyle: React.CSSProperties = {
            backgroundColor: '#fff',
            borderRadius: 5,
            maxWidth: 360,
            height: 'auto',
            padding: 0,
            border: '1px solid #000'
        };

        // Matches the OrchardDialog header style
        const headerStyles: React.CSSProperties = {
            border: '1px solid black',
            fontWeight: 700,
            padding: '4.69px 11.733px',
            margin: 1,
            borderRadius: 4,
            minHeight: 16,
            cursor: 'move'
        };

        // "Curtain of death"
        const backdrop: React.CSSProperties = {
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(170, 170, 170, 0.3)',
            zIndex: 1001
        };

        const saveDisabled = this.state.tempActiveColumns.length === 0;

        return (
            <div style={backdrop}>
                <Draggable
                    axis="both"
                    handle=".handle"
                    defaultPosition={{
                        x: this.props.defaultX ? this.props.defaultX : 0,
                        y: this.props.defaultY ? this.props.defaultY : 0
                    }}
                    defaultClassName="no-select"
                >
                    <div style={dialogStyle} className="no-select">
                        <div
                            style={headerStyles}
                            className="ui-corner-all copia_color1_b handle no-select"
                        >
                            <span
                                className="ui-dialog-title no-select"
                                style={{ color: '#fff', fontSize: '1.1em' }}
                            >
                                Configure Columns
                            </span>
                        </div>
                        <div
                            style={{ height: '500px', overflowY: 'scroll' }}
                            className="no-select"
                        >
                            <SortableList
                                items={activeItems}
                                useDragHandle={true}
                                onSortEnd={sortEnd => this.onSortEnd(sortEnd)}
                                helperClass="sortableHelper"
                            />
                            <hr />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                {inactiveItems}
                            </div>
                        </div>
                        <hr />
                        <div>
                            <div
                                style={{
                                    textAlign: 'right',
                                    padding: '2px 8px 8px 0'
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => this.onReset()}
                                    className="ui-button ui-corner-all ui-widget"
                                    style={{ margin: 5 }}
                                    name="column-picker-reset"
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    onClick={() => this.onSave()}
                                    className={
                                        'ui-button ui-corner-all ui-widget' +
                                        (saveDisabled
                                            ? ' ui-state-disabled'
                                            : '')
                                    }
                                    style={{ margin: 5 }}
                                    name="column-picker-save"
                                    disabled={saveDisabled}
                                >
                                    Update
                                </button>
                                <button
                                    type="button"
                                    onClick={() => this.onCancel()}
                                    className="ui-button ui-corner-all ui-widget"
                                    style={{ margin: 5 }}
                                    name="column-picker-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        );
    }

    /**
     * Hide the ColumnPicker
     */
    hide() {
        this.setState({ visible: false });
    }

    /**
     * Show the ColumnPicker
     */
    show() {
        this.setState({ visible: true });
    }

    /**
     * Builds the ColumnInput component for the given column
     * @param column The column to build the input for
     */
    private buildColumnInput(column: ColumnOption): JSX.Element {
        return (
            <ColumnInput
                key={column.ordinal}
                active={column.active}
                name={column.name}
                required={column.required}
                onChange={checked => {
                    this.onColumnChange(checked, column);
                }}
            />
        );
    }

    /**
     * Handles the user checking/unchecking the column checkbox
     * @param checked If the checkbox is checked
     * @param column The column the change is occurring for
     */
    private onColumnChange(checked: boolean, column: ColumnOption) {
        column.active = checked;

        if (column.active) {
            column.order = this.state.tempActiveColumns.length;
            const activeColumns = [...this.state.tempActiveColumns, column];
            const inactiveColumns = [
                ...this.state.tempInactiveColumns
                    .filter(item => !item.active)
                    .sort(sortColumnsByName)
            ];
            this.setState({
                tempActiveColumns: activeColumns,
                tempInactiveColumns: inactiveColumns
            });
        } else {
            column.order = -1;
            const activeColumns = [
                ...this.state.tempActiveColumns
                    .filter(item => item.active)
                    .sort(sortColumnsByOrder)
            ];
            const inactiveColumns = [
                ...this.state.tempInactiveColumns,
                column
            ].sort(sortColumnsByName);

            this.reindexColumns(activeColumns);

            this.setState({
                tempActiveColumns: activeColumns,
                tempInactiveColumns: inactiveColumns
            });
        }
    }

    /**
     * The sort handler
     * @param sortEnd The SortEnd parameters from react-sortable-hoc
     */
    private onSortEnd(sortEnd: SortEnd) {
        const { oldIndex, newIndex } = sortEnd;
        this.swapItems(oldIndex, newIndex);
    }

    /**
     * Moves an item in the list after sorting (user drag)
     * @param oldIndex The old index of the item that was moved
     * @param newIndex The new index of the item that was moved
     */
    private swapItems(oldIndex: number, newIndex: number) {
        const items = arrayMove(
            this.state.tempActiveColumns,
            oldIndex,
            newIndex
        );
        // Reindex the orders
        this.reindexColumns(items);
        this.setState({
            tempActiveColumns: items
        });
    }

    private reindexColumns(items: ColumnOption[]) {
        for (let i = 0; i < items.length; i++) {
            items[i].order = i;
        }
    }

    /**
     * Click handler for the Cancel button
     * Restores the state from open
     */
    private onCancel() {
        const { activeColumns, inactiveColumns } = this.resetActiveState();

        this.setState({
            tempActiveColumns: [...activeColumns],
            tempInactiveColumns: [...inactiveColumns],
            visible: false
        });
    }

    /**
     * Resets the active state for each column
     * Though we have separate temp arrays, the
     * underlying objects in each array are the same in memory
     */
    private resetActiveState() {
        const activeColumns = this.state.activeColumns;

        const inactiveColumns = this.state.inactiveColumns;

        for (const activeColumn of activeColumns) {
            activeColumn.active = true;
        }

        for (const inactiveColumn of inactiveColumns) {
            inactiveColumn.active = false;
        }

        return { activeColumns, inactiveColumns };
    }

    /**
     * Click handler for the Save button
     */
    private onSave() {
        if (this.state.tempActiveColumns.length === 0) {
            return;
        }

        const activeColumns = [...this.state.tempActiveColumns];
        const inactiveColumns = [...this.state.tempInactiveColumns];
        this.props.onSave([...activeColumns, ...inactiveColumns]);
        this.setState({
            activeColumns,
            inactiveColumns,
            visible: false
        });
    }

    /**
     * Click handler for the Reset butotn
     */
    private onReset() {
        this.setState({ visible: false });
        this.props.onReset();
    }
}

/**
 * Represents a Column/ColumnRule in a DataTable
 */
export interface ColumnOption {
    /**
     * If the Column is currently displayed
     */
    active: boolean;
    /**
     * The name/header of the Column Options
     */
    name: string;
    /**
     * The order of the column in the list of active columns
     */
    order: number;
    /**
     * The ordinal of the Column's enum item
     */
    ordinal: number;
    /**
     * If the column is required
     */
    required: boolean;
}
